/**
 * @fileoverview Asset Service for managing company assets
 * This service provides a facade over the AssetRegister namespace
 * to simplify asset operations throughout the application.
 * 
 * The service uses a singleton pattern to maintain a single instance
 * of the Asset library across the application.
 */
import { AssetRegister } from '../js/external-js/variableModules/namespaces/AssetRegister.namespace';

/**
 * Gets the tenancy owner UID from the active tenancy
 * @returns {string | null} The tenancy owner UID or null if not available
 */
const getTenancyOwner = (): string | null => {
  return window.activeTenancy?.tenancyOwner || null;
};

/**
 * Replaces template placeholders in asset data with actual values
 * @param {AssetRegister.AssetEntity} asset - The asset to process
 * @returns {AssetRegister.AssetEntity} The processed asset with placeholders replaced
 */
const replaceAssetPlaceholders = (asset: AssetRegister.AssetEntity): AssetRegister.AssetEntity => {
  const tenancyOwner = getTenancyOwner();
  
  // Replace {{tenancy.owner}} placeholder in assetOwner field
  if (asset.assetOwner === '{{tenancy.owner}}' && tenancyOwner) {
    return {
      ...asset,
      assetOwner: tenancyOwner
    };
  }
  
  return asset;
};

/** Singleton instance of the Assets library */
let AssetsLib: typeof AssetRegister.Asset | null = null;

/** Cache for assets list to prevent repeated API calls */
let assetsCache: {
  data: AssetRegister.AssetEntity[] | null;
  timestamp: number;
  promise: Promise<AssetRegister.AssetEntity[]> | null;
} = {
  data: null,
  timestamp: 0,
  promise: null,
};

/** Cache TTL in milliseconds (5 minutes) */
const ASSETS_CACHE_TTL = 5 * 60 * 1000;

/**
 * Initializes the Assets library if not already initialized
 * 
 * @returns {typeof AssetRegister.Asset} The Assets library instance
 */
const initializeAssetsLib = (): typeof AssetRegister.Asset => {
  if (!AssetsLib) {
    AssetsLib = AssetRegister.Asset;
  }
  return AssetsLib;
};

/**
 * Clears the assets cache, forcing the next getAssets() call to fetch fresh data
 */
export const clearAssetsCache = (): void => {
  assetsCache = { data: null, timestamp: 0, promise: null };
};

/**
 * Retrieves all assets from the system with caching to prevent repeated API calls
 * 
 * @param {boolean} forceRefresh - If true, bypasses the cache and fetches fresh data
 * @returns {Promise<AssetRegister.AssetEntity[]>} A promise that resolves to an array of assets
 */
export const getAssets = async (forceRefresh = false): Promise<AssetRegister.AssetEntity[]> => {
  const now = Date.now();
  
  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && assetsCache.data && (now - assetsCache.timestamp) < ASSETS_CACHE_TTL) {
    return assetsCache.data;
  }
  
  // If a fetch is already in progress, wait for it (prevents duplicate concurrent requests)
  if (assetsCache.promise) {
    return assetsCache.promise;
  }
  
  // Start a new fetch
  const lib = initializeAssetsLib();
  assetsCache.promise = (async () => {
    try {
      const assets = await lib.Get();
      const processedAssets = (assets || []).map(asset => replaceAssetPlaceholders(asset));
      assetsCache.data = processedAssets;
      assetsCache.timestamp = Date.now();
      return processedAssets;
    } finally {
      assetsCache.promise = null;
    }
  })();
  
  return assetsCache.promise;
};

/**
 * Retrieves all assets of a specific type from the system
 * 
 * @param {string} type - The type of assets to retrieve (e.g., 'asset', 'supplier')
 * @returns {Promise<AssetRegister.AssetEntity[]>} A promise that resolves to an array of filtered assets
 */
export const getAssetsByType = async (type: string): Promise<AssetRegister.AssetEntity[]> => {
  const lib = initializeAssetsLib();
  const assets = await lib.Get(null, type);
  
  if (!assets) return [];
  
  if (Array.isArray(assets)) {
    return assets.map(asset => replaceAssetPlaceholders(asset));
  }
  
  // If it's a single instance, convert it to an array
  return [replaceAssetPlaceholders(assets.entity)];
};

/**
 * Retrieves a specific asset by its ID
 * 
 * @param {string} assetID - The asset ID to search for
 * @returns {Promise<AssetRegister.AssetInstance | undefined>} A promise that resolves to the found asset or undefined
 */
export const getAsset = async (assetID: string): Promise<AssetRegister.AssetInstance | undefined> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] getAsset', assetID);
  
  try {
    const asset = await lib.Get(assetID);
    console.log('[AssetService] Found asset:', asset ? 'Yes' : 'No');
    return asset;
  } catch (error) {
    console.error('[AssetService] Error getting asset:', error);
    return undefined;
  }
};

/**
 * Creates a new asset in the system
 * 
 * @param {Partial<AssetRegister.AssetEntity>} assetData - The asset data to create
 * @returns {Promise<{id: string} | undefined>} A promise that resolves to an object with the new asset ID
 */
export const createAsset = async (
  assetData: Partial<AssetRegister.AssetEntity>
): Promise<{id: string} | undefined> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] Creating new asset');
  
  try {
    // First, get all existing assets to find the next available ID
    const existingAssets = await getAssets();
    console.log('[AssetService] Existing assets count:', existingAssets.length);
    console.log('[AssetService] Existing asset IDs:', existingAssets.map(a => a.assetID));
    
    // Find the highest existing asset ID (handle both string and number types)
    let highestId = 0;
    existingAssets.forEach(asset => {
      // Handle both string and number assetID
      const assetIdNum = typeof asset.assetID === 'number' 
        ? asset.assetID 
        : parseInt(String(asset.assetID), 10);
      
      if (!isNaN(assetIdNum) && assetIdNum > highestId) {
        highestId = assetIdNum;
      }
    });
    
    // Generate the next available ID
    const nextId = (highestId + 1).toString();
    console.log('[AssetService] Highest existing ID:', highestId);
    console.log('[AssetService] Using next available asset ID:', nextId);
    
    // Call the Add method with the next available ID and asset data
    const response = await lib.Add(nextId, assetData);
    
    if (response && response.statusCode === 200) {
      // The backend may have assigned a different ID if there was a conflict
      const responseWithId = response as AssetRegister.AssetRegisterResponse & { assetID?: number };
      const actualId = responseWithId.assetID ? responseWithId.assetID.toString() : nextId;
      console.log('[AssetService] Asset created successfully with ID:', actualId);
      console.log('[AssetService] Backend response:', response.message);
      return { id: actualId };
    } else {
      console.error('[AssetService] Error response from API:', response);
      return undefined;
    }
  } catch (error) {
    console.error('[AssetService] Error creating asset:', error);
    return undefined;
  }
};

/**
 * Updates an existing asset in the system
 * 
 * @param {string} assetID - The ID of the asset to update
 * @param {Partial<AssetRegister.AssetEntity>} assetData - The asset data to update
 * @returns {Promise<AssetRegister.AssetRegisterResponse | undefined>} A promise that resolves to the API response
 */
export const updateAsset = async (
  assetID: string,
  assetData: Partial<AssetRegister.AssetEntity>
): Promise<AssetRegister.AssetRegisterResponse | undefined> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] updateAsset', assetID);
  
  try {
    const response = await lib.Edit(assetID, assetData);
    return response || undefined;
  } catch (error) {
    console.error('[AssetService] Error updating asset:', error);
    return undefined;
  }
};

/**
 * Deletes an asset from the system
 * 
 * @param {string} assetID - The ID of the asset to delete
 * @returns {Promise<AssetRegister.AssetRegisterResponse | undefined>} A promise that resolves to the API response
 */
export const deleteAsset = async (
  assetID: string
): Promise<AssetRegister.AssetRegisterResponse | undefined> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] deleteAsset', assetID);
  
  try {
    const response = await lib.Delete(assetID);
    return response || undefined;
  } catch (error) {
    console.error('[AssetService] Error deleting asset:', error);
    return undefined;
  }
};

/**
 * Retrieves the history for a specific asset
 * 
 * @param {string} assetID - The ID of the asset to get history for
 * @returns {Promise<AssetRegister.AssetHistoryItem[]>} A promise that resolves to an array of history items
 */
export const getAssetHistory = async (
  assetID: string
): Promise<AssetRegister.AssetHistoryItem[]> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] getAssetHistory', assetID);
  
  try {
    const history = await lib.GetHistory(assetID);
    return history || [];
  } catch (error) {
    console.error('[AssetService] Error getting asset history:', error);
    return [];
  }
};

/**
 * Retrieves the latest Upguard security score for an asset
 * 
 * @param {string} assetID - The ID of the asset to get the score for
 * @returns {Promise<string>} A promise that resolves to the score string (e.g., "713/950")
 */
export const getAssetUpguardScore = async (
  assetID: string
): Promise<string> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] getAssetUpguardScore', assetID);
  
  try {
    const score = await lib.GetLatestUpguardScore(assetID);
    return score;
  } catch (error) {
    console.error('[AssetService] Error getting asset Upguard score:', error);
    return "0/950";
  }
};

/**
 * Retrieves the backup history for a specific asset
 * 
 * @param {string} assetID - The ID of the asset to get backup history for
 * @returns {Promise<{[key: string]: any}>} A promise that resolves to the backup history map
 */
export const getAssetBackupHistory = async (
  assetID: string
): Promise<{[key: string]: any}> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] getAssetBackupHistory', assetID);
  
  try {
    const backupHistory = await lib.GetBackupHistory(assetID);
    return backupHistory || {};
  } catch (error) {
    console.error('[AssetService] Error getting asset backup history:', error);
    return {};
  }
};

/**
 * Retrieves asset menu data (categories, types, etc.)
 * 
 * @returns {Promise<any>} A promise that resolves to the menu data
 */
export const getAssetMenuData = async (): Promise<any> => {
  const lib = initializeAssetsLib();
  try {
    const menuData = await lib.Menu();
    return menuData || {};
  } catch (error) {
    console.error('[AssetService] Error getting asset menu data:', error);
    return {};
  }
};

/**
 * Retrieves all deleted assets from the system
 * 
 * @returns {Promise<AssetRegister.AssetEntity[]>} A promise that resolves to an array of deleted assets
 */
export const getDeletedAssets = async (): Promise<AssetRegister.AssetEntity[]> => {
  const lib = initializeAssetsLib();
  try {
    const allAssets = await lib.Get();
    return (allAssets || []).filter(asset => asset.status === 'Deleted');
  } catch (error) {
    console.error('[AssetService] Error getting deleted assets:', error);
    return [];
  }
};

/**
 * Recovers a deleted asset
 * 
 * @param {string} assetID - The ID of the asset to recover
 * @returns {Promise<AssetRegister.AssetRegisterResponse>} A promise that resolves to the API response
 * @throws {Error} If the asset cannot be recovered
 */
export const recoverAsset = async (assetID: string): Promise<AssetRegister.AssetRegisterResponse> => {
  const lib = initializeAssetsLib();
  console.log('[AssetService] Recovering asset:', assetID);
  
  try {
    // First get the current asset data
    const asset = await lib.Get(assetID);
    if (!asset) {
      throw new Error('Asset not found');
    }
    
    // Create update object with only the fields we want to change
    const updateData: Partial<AssetRegister.AssetEntity> = {
      status: 'Active',
      // Remove any deletion markers if they exist
      ...(asset.entity as any).dateDeleted && { dateDeleted: undefined },
      ...(asset.entity as any).deletedBy && { deletedBy: undefined }
    };
    
    // Update the asset using the Edit method
    const response = await lib.Edit(assetID, updateData);
    
    if (!response) {
      throw new Error('No response from server');
    }
    
    console.log('[AssetService] Asset recovered successfully');
    return response;
  } catch (error) {
    console.error('[AssetService] Error recovering asset:', error);
    throw error;
  }
};

/** Default export of all asset service functions */
export default {
  getAssets,
  getAssetsByType,
  getAsset,
  createAsset,
  updateAsset,
  deleteAsset,
  getAssetHistory,
  getAssetUpguardScore,
  getAssetBackupHistory,
  getAssetMenuData,
  clearAssetsCache
};
